export type NcvetQuestionBankAccess = {
  rootAdmin: boolean;
  trainingCenterAdmin: boolean;
  awardingBody: boolean;
  assessorAdmin: boolean;
  assessor: boolean;
};

export const DEFAULT_NCVET_QUESTION_BANK_ACCESS: NcvetQuestionBankAccess = {
  rootAdmin: true,
  trainingCenterAdmin: true,
  awardingBody: true,
  assessorAdmin: true,
  assessor: true
};

/** Super Admin single choice: maps to a full ncvetQuestionBankAccess preset on save. */
export type NcvetQuestionBankTier = 'main' | 'awardingBody' | 'trainingCenter' | 'assessorAgency';

const TIER_KEYS = ['rootAdmin', 'trainingCenterAdmin', 'awardingBody', 'assessorAdmin', 'assessor'] as const;

/** Strict flags for tier UI (missing keys = false; avoids merge defaults breaking inference). */
function accessFlagsForInference(raw: Partial<NcvetQuestionBankAccess> | Record<string, unknown> | null | undefined): NcvetQuestionBankAccess {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    rootAdmin: r.rootAdmin === true,
    trainingCenterAdmin: r.trainingCenterAdmin === true,
    awardingBody: r.awardingBody === true,
    assessorAdmin: r.assessorAdmin === true,
    assessor: r.assessor === true
  };
}

function matchesPreset(m: NcvetQuestionBankAccess, preset: NcvetQuestionBankAccess): boolean {
  return TIER_KEYS.every((k) => Boolean(m[k]) === Boolean(preset[k]));
}

/** Deprecated UI tier: training centre + assessor admin + assessor (still infer for existing orgs). */
function legacyDeliveryPreset(): NcvetQuestionBankAccess {
  return {
    rootAdmin: false,
    trainingCenterAdmin: true,
    awardingBody: false,
    assessorAdmin: true,
    assessor: true
  };
}

/** Preset written to the organization document (API shape unchanged). */
export function presetNcvetQuestionBankAccess(tier: NcvetQuestionBankTier): NcvetQuestionBankAccess {
  switch (tier) {
    case 'main':
      return {
        rootAdmin: true,
        trainingCenterAdmin: false,
        awardingBody: false,
        assessorAdmin: false,
        assessor: false
      };
    case 'awardingBody':
      return {
        rootAdmin: false,
        trainingCenterAdmin: false,
        awardingBody: true,
        assessorAdmin: false,
        assessor: false
      };
    case 'trainingCenter':
      return {
        rootAdmin: false,
        trainingCenterAdmin: true,
        awardingBody: false,
        assessorAdmin: false,
        assessor: false
      };
    case 'assessorAgency':
      return {
        rootAdmin: false,
        trainingCenterAdmin: false,
        awardingBody: false,
        assessorAdmin: true,
        assessor: true
      };
  }
}

/**
 * Infer tier from stored access flags. Uses explicit `=== true` for heuristics so partial documents behave.
 * Legacy "all true" / old combined delivery preset is mapped to a sensible default for the new four-tier UI.
 */
export function inferNcvetQuestionBankTier(raw: Partial<NcvetQuestionBankAccess> | Record<string, unknown> | null | undefined): NcvetQuestionBankTier {
  const n = accessFlagsForInference(raw);
  const allOn =
    n.rootAdmin && n.trainingCenterAdmin && n.awardingBody && n.assessorAdmin && n.assessor;
  if (allOn) return 'main';

  if (matchesPreset(n, presetNcvetQuestionBankAccess('main'))) return 'main';
  if (matchesPreset(n, presetNcvetQuestionBankAccess('awardingBody'))) return 'awardingBody';
  if (matchesPreset(n, presetNcvetQuestionBankAccess('trainingCenter'))) return 'trainingCenter';
  if (matchesPreset(n, presetNcvetQuestionBankAccess('assessorAgency'))) return 'assessorAgency';
  if (matchesPreset(n, legacyDeliveryPreset())) return 'trainingCenter';

  if (n.trainingCenterAdmin && !n.assessorAdmin && !n.assessor) return 'trainingCenter';
  if ((n.assessorAdmin || n.assessor) && !n.trainingCenterAdmin) return 'assessorAgency';
  if (n.trainingCenterAdmin && (n.assessorAdmin || n.assessor)) return 'trainingCenter';

  if (n.awardingBody) return 'awardingBody';
  if (n.rootAdmin) return 'main';
  return 'main';
}

export const NCVET_QUESTION_BANK_TIER_OPTIONS: {
  value: NcvetQuestionBankTier;
  label: string;
  description: string;
}[] = [
  {
    value: 'main',
    label: 'Main organization',
    description: 'Only root organization admins manage the question bank and rubrics.'
  },
  {
    value: 'awardingBody',
    label: 'Awarding body',
    description: 'Awarding body users manage content; main organization admins retain access per NCVET hierarchy.'
  },
  {
    value: 'trainingCenter',
    label: 'Training centre',
    description: 'Training centre (child org) admins manage content; main organization admins automatically retain access.'
  },
  {
    value: 'assessorAgency',
    label: 'Assessor agency and assessor',
    description: 'Assessor agency admins and assessors manage content; main organization admins automatically retain access.'
  }
];

export function mergeNcvetQuestionBankAccess(raw: Partial<NcvetQuestionBankAccess> | null | undefined): NcvetQuestionBankAccess {
  return { ...DEFAULT_NCVET_QUESTION_BANK_ACCESS, ...(raw || {}) };
}

function accessKeyForUser(userType: string | undefined, parentOrganizationId: unknown): keyof NcvetQuestionBankAccess | null {
  if (!userType) return null;
  if (userType === 'admin') {
    return parentOrganizationId ? 'trainingCenterAdmin' : 'rootAdmin';
  }
  if (userType === 'awardingBody' || userType === 'awarding-body') return 'awardingBody';
  if (userType === 'assessorAdmin') return 'assessorAdmin';
  if (userType === 'assessor') return 'assessor';
  return null;
}

/** NCVET compliance on this org or inherited from root (matches server effectiveNcvetCompliance). */
export function isOrganizationNcvetEffective(organization: any): boolean {
  if (!organization) return false;
  if (typeof organization.effectiveNcvetCompliance === 'boolean') return organization.effectiveNcvetCompliance;
  return Boolean(organization.ncvetCompliance);
}

/** Question bank + rubrics UI/API gate: subtree-merged + upward-propagated access from server (effectiveNcvetQuestionBankAccess). */
export function canManageNcvetQuestionBankContent(organization: any, user: any): boolean {
  if (user?.type === 'superadmin') return true;
  if (!isOrganizationNcvetEffective(organization)) return false;
  const key = accessKeyForUser(user?.type, organization?.parentOrganizationId);
  if (!key) return false;
  const rawAccess =
    organization?.effectiveNcvetQuestionBankAccess ?? organization?.ncvetQuestionBankAccess;
  const access = mergeNcvetQuestionBankAccess(rawAccess);
  return access[key] === true;
}

/**
 * Explicit org NCVET bank flags grant assessor agency / assessor only (no main / awarding body / TC bank tier).
 * Matches server isNcvetBankAssessorDeliveryOnlyAsync for UI (save template before questions exist).
 */
export function isNcvetBankAssessorDeliveryOnly(organization: any): boolean {
  const ex = organization?.explicitNcvetQuestionBankAccess ?? organization?.ncvetQuestionBankAccess ?? {};
  const on = (k: keyof NcvetQuestionBankAccess) => ex[k] === true;
  return (on('assessorAdmin') || on('assessor')) && !on('rootAdmin') && !on('awardingBody') && !on('trainingCenterAdmin');
}

/**
 * Explicit org NCVET bank flags grant training centre admin only (matches server isNcvetBankTrainingCenterOnlyAsync).
 */
export function isNcvetBankTrainingCenterOnly(organization: any): boolean {
  const ex = organization?.explicitNcvetQuestionBankAccess ?? organization?.ncvetQuestionBankAccess ?? {};
  const on = (k: keyof NcvetQuestionBankAccess) => ex[k] === true;
  return (
    on('trainingCenterAdmin') &&
    !on('rootAdmin') &&
    !on('awardingBody') &&
    !on('assessorAdmin') &&
    !on('assessor')
  );
}

